import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { isActiveLink } from "@/components/sidebar-items";
import type { SidebarItem } from "@/components/sidebar-items";

interface SidebarMainPanelProps {
  /** When set, renders the onboarding link in its own group above everything. */
  onboardingItem?: SidebarItem;
  primaryAgentUrl: string;
  primaryAgentLabel: string;
  PrimaryAgentIcon: LucideIcon;
  primaryAgentIsSelected: boolean;
  primaryItems: SidebarItem[];
  /** Items needing attention shown on the Inbox badge: agent tasks pending review plus the user's open onboarding tasks. */
  inboxCount: number;
  recordsItems: SidebarItem[];
  isRecordsOpen: boolean;
  onRecordsOpenChange: (open: boolean) => void;
  pathname: string;
  search: string;
}

/**
 * Main navigation panel for the sidebar, showing the primary agent link,
 * top-level nav items, and a collapsible Records section.
 */
export function SidebarMainPanel({
  onboardingItem,
  primaryAgentUrl,
  primaryAgentLabel,
  PrimaryAgentIcon,
  primaryAgentIsSelected,
  primaryItems,
  inboxCount,
  recordsItems,
  isRecordsOpen,
  onRecordsOpenChange,
  pathname,
  search,
}: SidebarMainPanelProps) {
  return (
    <div className="flex w-1/2 flex-col overflow-y-auto scrollbar-transparent">
      {onboardingItem && (
        <SidebarGroup className="border-b border-sidebar-border pb-1">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActiveLink(onboardingItem.url, pathname, search)}
                >
                  <Link to={onboardingItem.url}>
                    <onboardingItem.icon className="text-primary" />
                    <span>{onboardingItem.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={primaryAgentIsSelected}>
                <Link to={primaryAgentUrl}>
                  <PrimaryAgentIcon />
                  <span>{primaryAgentLabel}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {primaryItems.map((item) => (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={isActiveLink(item.url, pathname, search)}
                >
                  <Link to={item.url} className="relative">
                    <item.icon />
                    <span>{item.title}</span>
                    {item.title === "Inbox" && inboxCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="sidebar-badge-pulse ml-auto h-5 min-w-5 px-1.5 text-xs"
                      >
                        {inboxCount}
                      </Badge>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <Collapsible open={isRecordsOpen} onOpenChange={onRecordsOpenChange}>
        <SidebarGroup>
          <CollapsibleTrigger asChild>
            <SidebarGroupLabel className="cursor-pointer uppercase tracking-widest text-[10px] font-semibold hover:bg-sidebar-accent">
              <span className="flex-1">Records</span>
              <ChevronDown
                className={`transition-transform duration-150 ease-out ${isRecordsOpen ? "" : "-rotate-90"}`}
              />
            </SidebarGroupLabel>
          </CollapsibleTrigger>
          <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
            <SidebarGroupContent>
              <SidebarMenu>
                {recordsItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActiveLink(item.url, pathname, search)}
                    >
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>
    </div>
  );
}
